import { type ChangeEvent, type FormEvent, useEffect, useRef, useState } from 'react'
import {
  Check,
  Clipboard,
  ClipboardPaste,
  ImageUp,
  Library,
  ListFilter,
  Loader2,
  Metronome,
  Music,
  Pencil,
  RefreshCw,
  Save,
  Search,
  Sparkles,
  Trash2,
  TriangleAlert,
  Unlink,
  X
} from 'lucide-react'
import {
  formatBytes,
  getAudioImageUrl,
  resolveMediaUrl,
  type AudioAnalysisSummary,
  type AudioGroup,
  type AudioTrack
} from '../../../api'
import {
  type AudioTrackLabel,
  type AudioTrackLabelSaveInput,
  type AudioTrackLabelStateResult,
  type AudioTrackLabelSuggestion,
  type Label,
  type LabelCategory
} from '../../../api/labels'
import {
  suggestSunoPromptMetadata,
  type SunoPromptGroup
} from '../../../api/suno'
import { Button, ChipSelect, IconButton, LabelBrowser, Modal, ModalActions, StatusBadge, labelBrowserClassName } from '../../../components/ui'
import deleteConfirmStyles from '../../../components/DeleteConfirmDialog/DeleteConfirmDialog.module.css'
import uploadStyles from '../../../components/ui/uploadDialogStyles.module.css'
import { labelCategoryLabels, labelCategoryOptions } from '../../labels/labelDisplay'
import { labelUiClass, labelUiStyles } from '../../labels/labelUiStyles'
import { normalizeLabelText } from '../../labels/labelText'
import { emptyPromptEditor, PromptEditorDialog, type PromptEditorState } from '../../suno/PromptEditorDialog'
import { splitStylePrompt } from '../../suno/parseStylePrompt'
import { getAudioTrackMetadataItems } from '../formatAudioMetadata'
import { useTapBpm } from '../useTapBpm'
import { formatBpmValue, metadataIcons, trackEditorHeaderMeta } from './AudioTrackDialog.helpers'
import { AudioTrackMobileSheet } from './AudioTrackMobileSheet'
import styles from './AudioTrackDialog.module.css'
import suggestionStyles from '../TrackLabelSuggestions.module.css'
import { useTrackLabels } from './useTrackLabels'
import { getAudioAnalysisMessage, useTrackLabelSuggestions } from './useTrackLabelSuggestions'

const lb = labelBrowserClassName
const css = (...classNames: Array<string | false | null | undefined>): string => classNames.filter(Boolean).join(' ')

type AudioTrackDialogProps = {
  groups: AudioGroup[]
  labels: Label[]
  promptGroups: SunoPromptGroup[]
  track: AudioTrack
  onClose: () => void
  onSave: (input: {
    id: string
    title: string
    artist: string | null
    audioGroupIds: string[]
    labels: AudioTrackLabelSaveInput[]
    manual_bpm: number | null
    manual_bpm_source: 'number' | 'tap' | null
    style_prompt: string | null
  }) => Promise<void>
  onDeleteImage: (trackId: string) => Promise<AudioTrack>
  onCheckPromptLibrary: (input: {
    artist: string | null
    id: string
    style_prompt: string
    title: string
  }) => Promise<AudioTrack>
  onLoadTrackLabels: (trackId: string) => Promise<AudioTrackLabelStateResult>
  onRejectLabel: (trackId: string, assignmentId: string) => Promise<void>
  onAnalyzeAudio: (trackId: string) => Promise<AudioAnalysisSummary>
  onDeleteLosslessSource: (trackId: string) => Promise<AudioTrack>
  onSavePromptFromTrack: (input: {
    prompt: PromptEditorState
    trackId: string
  }) => Promise<AudioTrack>
  onSuggestLabels: (trackId: string, stylePrompt: string) => Promise<AudioTrackLabelSuggestion[]>
  onUnlinkSunoPrompt: (trackId: string) => Promise<AudioTrack>
  onUploadImage: (trackId: string, file: File) => Promise<AudioTrack>
  onUploadLosslessSource: (trackId: string, file: File) => Promise<AudioTrack>
}

const cssClass = (name: keyof typeof styles): string => {
  const className = styles[name]

  if (!className) {
    throw new Error(`Missing AudioTrackDialog CSS module class: ${name}`)
  }

  return className
}

const audioAnalysisStatusClass = (status: AudioAnalysisSummary['status']): string => {
  if (status === 'failed') {
    return cssClass('statusFailed')
  }

  if (status === 'completed') {
    return cssClass('statusCompleted')
  }

  if (status === 'queued') {
    return cssClass('statusQueued')
  }

  if (status === 'processing') {
    return cssClass('statusProcessing')
  }

  return ''
}

const uploadClass = (...classNames: Array<string | false | null | undefined>): string =>
  classNames
    .filter(Boolean)
    .map((className) => uploadStyles[className as string] ?? className)
    .join(' ')

export const AudioTrackDialog = (props: AudioTrackDialogProps) => {
  const imageInputRef = useRef<HTMLInputElement | null>(null)
  const losslessInputRef = useRef<HTMLInputElement | null>(null)
  const mobileImageInputRef = useRef<HTMLInputElement | null>(null)
  const mobileLosslessInputRef = useRef<HTMLInputElement | null>(null)
  const stylePromptRef = useRef<HTMLTextAreaElement | null>(null)
  const [title, setTitle] = useState(props.track.title)
  const [artist, setArtist] = useState(props.track.artist ?? '')
  const [manualBpmInput, setManualBpmInput] = useState(props.track.manual_bpm?.toString() ?? '')
  const [manualBpmSource, setManualBpmSource] = useState<'number' | 'tap' | null>(props.track.manual_bpm_source)
  const { tapBpm, tapBpmBeat, tapCount } = useTapBpm()
  const [stylePrompt, setStylePrompt] = useState(props.track.style_prompt ?? '')
  const [groupIds, setGroupIds] = useState(props.track.groups.map((group) => group.id))
  const toggleGroup = (groupId: string): void => {
    setGroupIds((current) =>
      current.includes(groupId)
        ? current.filter((id) => id !== groupId)
        : [...current, groupId]
    )
  }
  const [labelAssignments, setLabelAssignments] = useState<AudioTrackLabelSaveInput[]>(
    props.track.labels.map((label) => ({
      labelId: label.id,
      source: label.source === 'prompt_llm' || label.source === 'audio_local' || label.source === 'audio_ai'
        ? label.source
        : 'manual'
    }))
  )
  const [labelSearch, setLabelSearch] = useState('')
  const [labelBrowserOpen, setLabelBrowserOpen] = useState(false)
  const [browseSelectedLabelIds, setBrowseSelectedLabelIds] = useState<string[]>([])
  const [browseCategory, setBrowseCategory] = useState<LabelCategory | 'all'>('all')
  const [labelClipboardMessage, setLabelClipboardMessage] = useState<string | null>(null)
  const [suggestionsDialogOpen, setSuggestionsDialogOpen] = useState(false)
  const [trackWithImage, setTrackWithImage] = useState(props.track)
  const imageUrl = getAudioImageUrl(trackWithImage, 'preview')
  const [saving, setSaving] = useState(false)
  const [checkingPromptLibrary, setCheckingPromptLibrary] = useState(false)
  const [promptLibraryMessage, setPromptLibraryMessage] = useState<string | null>(null)
  const [promptEditorState, setPromptEditorState] = useState<PromptEditorState | null>(null)
  const [promptEditorError, setPromptEditorError] = useState<string | null>(null)
  const [savingPrompt, setSavingPrompt] = useState(false)
  const [unlinking, setUnlinking] = useState(false)
  const [isEditingLinkedPrompt, setIsEditingLinkedPrompt] = useState(false)
  const [updatingImage, setUpdatingImage] = useState(false)
  const [uploadingLossless, setUploadingLossless] = useState(false)
  const [deletingLossless, setDeletingLossless] = useState(false)
  const [losslessDeleteDialogOpen, setLosslessDeleteDialogOpen] = useState(false)
  const [losslessUploadError, setLosslessUploadError] = useState<string | null>(null)
  const [mobileEditorTab, setMobileEditorTab] = useState<'info' | 'flac' | 'bindings'>('info')
  const [tapPulseKey, setTapPulseKey] = useState(0)

  useEffect(() => {
    setTrackWithImage(props.track)
  }, [props.track])
  const metadataItems = getAudioTrackMetadataItems(props.track)
  const bpmHelperText = props.track.effective_bpm !== null && props.track.detected_bpm !== null
    ? `Detected: ${formatBpmValue(props.track.detected_bpm)} BPM · Saved: ${formatBpmValue(props.track.effective_bpm)} BPM`
    : props.track.effective_bpm !== null
      ? `Saved: ${formatBpmValue(props.track.effective_bpm)} BPM`
      : props.track.detected_bpm !== null
        ? `Detected: ${formatBpmValue(props.track.detected_bpm)} BPM`
        : 'Optional manual BPM for channel filtering.'
  const losslessStatusLabel = trackWithImage.lossless_status === 'ready'
    ? 'Ready'
    : trackWithImage.lossless_status === 'processing'
      ? 'Processing'
      : trackWithImage.lossless_status === 'failed'
        ? 'Failed'
        : 'Missing'
  const losslessFileName = trackWithImage.lossless_object_key
    ? trackWithImage.lossless_object_key.split(/[\\/]/).filter(Boolean).pop() ?? 'lossless source'
    : null
  const labelIds = labelAssignments.map((label) => label.labelId)
  const selectedLabels = props.labels
    .filter((label) => labelIds.includes(label.id))
    .sort((left, right) => {
      const categoryDiff = labelCategoryOptions.indexOf(left.category) - labelCategoryOptions.indexOf(right.category)

      return categoryDiff || left.name.localeCompare(right.name)
    })
  const groupedSelectedLabels = labelCategoryOptions
    .map((category) => ({
      category,
      items: selectedLabels.filter((label) => label.category === category)
    }))
    .filter((group) => group.items.length > 0)
  const normalizedLabelSearch = labelSearch.trim().toLowerCase()
  const currentStylePrompt = stylePrompt.trim()
  const tapButtonLabel = tapCount > 0 && tapCount <= 4 ? String(5 - tapCount) : 'TAP'
  const handleTapBpmBeat = (): void => {
    tapBpmBeat()
    setTapPulseKey((current) => current + 1)
  }
  const {
    audioAnalysis,
    clearPendingRejectedSuggestion,
    loadingTrackLabels,
    loadTrackLabels,
    markSuggestionRejected,
    pendingRejectedSuggestionIds,
    setAudioAnalysis,
    trackLabels
  } = useTrackLabels({
    initialAudioAnalysis: props.track.audio_analysis,
    onLoadTrackLabels: props.onLoadTrackLabels,
    trackId: props.track.id
  })
  const {
    analyzeAudio,
    applySelectedSuggestions,
    audioAnalysisMessage,
    audioAnalysisPolling,
    canSuggestLabels,
    labelSuggestions,
    loadingSuggestions,
    selectedSuggestionIds,
    setSelectedSuggestionIds,
    suggestionError,
    suggestLabels
  } = useTrackLabelSuggestions({
    currentStylePrompt,
    labelIds,
    loadTrackLabels,
    onAnalyzeAudio: props.onAnalyzeAudio,
    onApplySuggestions: (items) => {
      setLabelAssignments((current) => [...current, ...items])
    },
    onSetAudioAnalysis: setAudioAnalysis,
    onSuggestLabels: props.onSuggestLabels,
    suggestionsDialogOpen,
    trackId: props.track.id
  })
  const canCheckPromptLibrary = Boolean(currentStylePrompt) && Boolean(title.trim()) && !checkingPromptLibrary
  const suggestedTrackLabels = trackLabels.filter((assignment) =>
    assignment.status === 'suggested'
    && !labelIds.includes(assignment.label_id)
    && !pendingRejectedSuggestionIds.includes(assignment.id)
  )
  const suggestionCount = suggestedTrackLabels.length + labelSuggestions.length
  const audioSuggestionCount = suggestedTrackLabels.filter((assignment) => assignment.source === 'audio_ai').length
  const browseAddedCount = browseSelectedLabelIds.filter((labelId) => !labelIds.includes(labelId)).length
  const parseManualBpmInput = (): number | null => {
    const trimmed = manualBpmInput.trim()

    if (!trimmed) {
      return null
    }

    const parsed = Number(trimmed)

    return Number.isFinite(parsed) ? parsed : Number.NaN
  }
  const manualBpm = parseManualBpmInput()
  const manualBpmInvalid = Number.isNaN(manualBpm) || (manualBpm !== null && (manualBpm < 40 || manualBpm > 220))
  const matchesLabelSearch = (label: Label): boolean =>
    !normalizedLabelSearch
    || normalizeLabelText(label.name).includes(normalizedLabelSearch)
    || normalizeLabelText(label.slug).includes(normalizedLabelSearch)
    || label.aliases.some((alias) => normalizeLabelText(alias).includes(normalizedLabelSearch))
  const findLabelFromClipboardToken = (token: string): Label | undefined => {
    const normalizedToken = normalizeLabelText(token)

    if (!normalizedToken) {
      return undefined
    }

    return props.labels.find((label) =>
      label.status === 'active'
      && (
        normalizeLabelText(label.name) === normalizedToken
        || normalizeLabelText(label.slug) === normalizedToken
        || label.aliases.some((alias) => normalizeLabelText(alias) === normalizedToken)
      )
    )
  }

  const saveTrack = async (): Promise<void> => {
    if (manualBpmInvalid) {
      return
    }

    setSaving(true)
    try {
      await Promise.all(
        pendingRejectedSuggestionIds.map((assignmentId) => props.onRejectLabel(props.track.id, assignmentId))
      )
      await props.onSave({
        id: props.track.id,
        title,
        artist: artist.trim() || null,
        audioGroupIds: groupIds,
        labels: labelAssignments,
        manual_bpm: manualBpm,
        manual_bpm_source: manualBpm === null ? null : manualBpmSource ?? 'number',
        style_prompt: stylePrompt.trim() || null
      })
    } finally {
      setSaving(false)
    }
  }

  const save = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault()
    await saveTrack()
  }

  const checkPromptLibrary = async (): Promise<void> => {
    if (!currentStylePrompt) {
      setPromptLibraryMessage('Add a Suno style prompt before checking the library.')
      return
    }

    setCheckingPromptLibrary(true)
    setPromptLibraryMessage(null)

    try {
      const updatedTrack = await props.onCheckPromptLibrary({
        artist: artist.trim() || null,
        id: props.track.id,
        style_prompt: currentStylePrompt,
        title: title.trim()
      })
      setTrackWithImage(updatedTrack)
      setPromptLibraryMessage(`Linked "${updatedTrack.suno_prompt_binding?.prompt_snapshot.title ?? 'library prompt'}".`)
      setIsEditingLinkedPrompt(false)
    } catch (error) {
      setPromptLibraryMessage(error instanceof Error ? error.message : 'Unable to check Prompt Library.')
    } finally {
      setCheckingPromptLibrary(false)
    }
  }

  const openSavePromptDialog = (): void => {
    if (!currentStylePrompt) {
      return
    }

    const parsed = splitStylePrompt(currentStylePrompt)
    setPromptEditorError(null)
    setPromptEditorState({
      ...emptyPromptEditor(),
      excludePrompt: parsed.excludePrompt,
      stylePrompt: parsed.stylePrompt
    })
  }

  const savePromptFromTrack = async (input: PromptEditorState): Promise<void> => {
    setSavingPrompt(true)
    setPromptEditorError(null)

    try {
      const updatedTrack = await props.onSavePromptFromTrack({
        prompt: input,
        trackId: props.track.id
      })
      setTrackWithImage(updatedTrack)
      setPromptEditorState(null)
      setPromptLibraryMessage(`Created and linked "${updatedTrack.suno_prompt_binding?.prompt_snapshot.title ?? 'library prompt'}".`)
      setIsEditingLinkedPrompt(false)
    } catch (error) {
      setPromptEditorError(error instanceof Error ? error.message : 'Unable to save prompt.')
    } finally {
      setSavingPrompt(false)
    }
  }

  const unlinkPrompt = async (): Promise<void> => {
    setUnlinking(true)
    setPromptLibraryMessage(null)
    try {
      const updatedTrack = await props.onUnlinkSunoPrompt(props.track.id)
      setTrackWithImage(updatedTrack)
      setIsEditingLinkedPrompt(false)
      setPromptLibraryMessage('Unlinked from library prompt.')
    } catch (error) {
      setPromptLibraryMessage(error instanceof Error ? error.message : 'Unable to unlink prompt.')
    } finally {
      setUnlinking(false)
    }
  }

  const enterPromptEditMode = (): void => {
    setIsEditingLinkedPrompt(true)
  }

  const resizeStylePromptInput = (): void => {
    const input = stylePromptRef.current

    if (!input) {
      return
    }

    input.style.height = 'auto'
    input.style.overflowY = 'hidden'

    const maxHeightPx = 220
    const nextHeight = Math.min(input.scrollHeight, maxHeightPx)

    input.style.height = `${nextHeight}px`
    if (input.scrollHeight > maxHeightPx) {
      input.style.overflowY = 'auto'
    }
  }

  useEffect(() => {
    resizeStylePromptInput()
  }, [stylePrompt])

  useEffect(() => {
    if (tapBpm.bpm !== null) {
      setManualBpmInput(formatBpmValue(tapBpm.bpm))
      setManualBpmSource('tap')
    }
  }, [tapBpm.bpm])

  useEffect(() => {
    if (!labelBrowserOpen && !suggestionsDialogOpen) {
      return undefined
    }

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    return () => {
      document.body.style.overflow = previousOverflow
    }
  }, [labelBrowserOpen, suggestionsDialogOpen])

  const addManualLabels = (newLabelIds: string[]): void => {
    setLabelAssignments((current) => {
      const existingIds = new Set(current.map((label) => label.labelId))
      const additions = newLabelIds
        .filter((labelId) => !existingIds.has(labelId))
        .map((labelId) => ({
          labelId,
          source: 'manual' as const,
          weight: 1
        }))

      return additions.length > 0 ? [...current, ...additions] : current
    })
  }

  const openLabelBrowser = (): void => {
    setBrowseSelectedLabelIds(labelIds)
    setBrowseCategory('all')
    setLabelSearch('')
    setLabelBrowserOpen(true)
  }

  const closeLabelBrowser = (): void => {
    setLabelBrowserOpen(false)
    setLabelSearch('')
    setBrowseCategory('all')
    setBrowseSelectedLabelIds([])
  }

  const applyBrowseLabels = (): void => {
    addManualLabels(browseSelectedLabelIds)
    closeLabelBrowser()
  }

  const acceptPersistedSuggestion = (assignment: AudioTrackLabel): void => {
    setLabelAssignments((current) => {
      if (current.some((item) => item.labelId === assignment.label_id)) {
        return current
      }

      return [
        ...current,
        {
          labelId: assignment.label_id,
          source: assignment.source === 'audio_ai' || assignment.source === 'prompt_llm' || assignment.source === 'audio_local'
            ? assignment.source
            : 'manual',
          confidence: assignment.confidence ? Number(assignment.confidence) : null,
          weight: assignment.weight ? Number(assignment.weight) : 1,
          reason: assignment.reason
        }
      ]
    })
    clearPendingRejectedSuggestion(assignment.id)
  }

  const rejectPersistedSuggestion = (assignment: AudioTrackLabel): void => {
    markSuggestionRejected(assignment.id)
  }

  const copySelectedLabels = async (): Promise<void> => {
    if (selectedLabels.length === 0) {
      return
    }

    await navigator.clipboard.writeText(selectedLabels.map((label) => label.name).join(', '))
    setLabelClipboardMessage(`Copied ${selectedLabels.length} label(s).`)
  }

  const pasteLabels = async (): Promise<void> => {
    const clipboardText = await navigator.clipboard.readText()
    const tokens = clipboardText
      .split(/[\n,;|]+/)
      .map((token) => token.trim())
      .filter(Boolean)
    const matchedLabels = tokens
      .map(findLabelFromClipboardToken)
      .filter((label): label is Label => Boolean(label))
    const uniqueMatchedIds = Array.from(new Set(matchedLabels.map((label) => label.id)))

    addManualLabels(uniqueMatchedIds)

    const unmatchedCount = Math.max(tokens.length - uniqueMatchedIds.length, 0)
    setLabelClipboardMessage(
      uniqueMatchedIds.length > 0
        ? `Pasted ${uniqueMatchedIds.length} label(s)${unmatchedCount > 0 ? `, ${unmatchedCount} not matched.` : '.'}`
        : 'No matching active labels found in clipboard.'
    )
  }

  const uploadImage = async (event: ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = event.target.files?.[0]

    if (!file) {
      return
    }

    setUpdatingImage(true)
    try {
      const track = await props.onUploadImage(props.track.id, file)
      setTrackWithImage(track)
    } finally {
      setUpdatingImage(false)
      event.target.value = ''
    }
  }

  const deleteImage = async (): Promise<void> => {
    setUpdatingImage(true)
    try {
      const track = await props.onDeleteImage(props.track.id)
      setTrackWithImage(track)
    } finally {
      setUpdatingImage(false)
    }
  }

  const uploadLosslessSource = async (event: ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = event.target.files?.[0]

    if (!file) {
      return
    }

    setUploadingLossless(true)
    setLosslessUploadError(null)

    try {
      const track = await props.onUploadLosslessSource(props.track.id, file)
      setTrackWithImage(track)
    } catch (error) {
      setLosslessUploadError(error instanceof Error ? error.message : 'Unable to upload lossless source.')
    } finally {
      setUploadingLossless(false)
      event.target.value = ''
    }
  }

  const deleteLosslessSource = async (): Promise<void> => {
    setDeletingLossless(true)
    setLosslessUploadError(null)

    try {
      const track = await props.onDeleteLosslessSource(props.track.id)
      setTrackWithImage(track)
      setLosslessDeleteDialogOpen(false)
    } catch (error) {
      setLosslessUploadError(error instanceof Error ? error.message : 'Unable to remove FLAC.')
    } finally {
      setDeletingLossless(false)
    }
  }

  return (
    <div className={`modal-backdrop audio-track-modal-backdrop ${styles.backdrop}`} onMouseDown={(event) => { if (event.target === event.currentTarget) props.onClose() }}>
      <form className={`modal-panel ${styles.editorPanel} ${styles.desktopForm}`} onSubmit={(event) => void save(event)}>
        <div className={styles.editorTopbar}>
          <div className={styles.editorHeadingCopy}>
            <h2>Edit track</h2>
            <p className={styles.editorHeaderMeta}>
              {trackEditorHeaderMeta(props.track) ?? (
                <span className="muted">Metadata, groups, and labels</span>
              )}
            </p>
          </div>
          <div className={styles.editorHeadingActions}>
            <StatusBadge label={props.track.status} />
            <button
              aria-label="Close track editor"
              className="modal-close-button"
              onClick={props.onClose}
              type="button"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        <div className={styles.editorWorkspace}>
          <div className={styles.editorMainColumn}>
            <div className={styles.editorSummary}>
              <div className={styles.artworkPanel}>
                <button
                  className={styles.imagePreview}
                  onClick={() => imageInputRef.current?.click()}
                  type="button"
                  aria-label="Upload track image"
                >
                  {imageUrl ? (
                    <img alt="" src={resolveMediaUrl(imageUrl)} />
                  ) : (
                    <Music size={26} />
                  )}
                </button>
                <div className={styles.artworkActions}>
                  <IconButton
                    disabled={updatingImage}
                    icon={<ImageUp size={16} />}
                    label={imageUrl ? 'Replace track image' : 'Upload track image'}
                    onClick={() => imageInputRef.current?.click()}
                    title={imageUrl ? 'Replace image' : 'Upload image'}
                    size="sm"
                  />
                  {imageUrl && (
                    <IconButton
                      disabled={updatingImage}
                      icon={<Trash2 size={16} />}
                      label="Remove track image"
                      onClick={() => void deleteImage()}
                      title="Remove image"
                      size="sm"
                      variant="danger"
                    />
                  )}
                </div>
                {updatingImage && <span className={styles.artworkStatus}>Updating...</span>}
                <input
                  accept="image/*"
                  hidden
                  onChange={(event) => void uploadImage(event)}
                  ref={imageInputRef}
                  type="file"
                />
              </div>
              <div className={styles.primaryFields}>
                <label className="form-group">
                  <span className="form-label">Title</span>
                  <input className="form-input" onChange={(event) => setTitle(event.target.value)} value={title} />
                </label>
                <label className="form-group">
                  <span className="form-label">Artist</span>
                  <input className="form-input" onChange={(event) => setArtist(event.target.value)} placeholder="Unknown artist" value={artist} />
                </label>
                <div className="form-group">
                  <span className="form-label">BPM</span>
                  <div className={styles.bpmRow}>
                    <input
                      className="form-input"
                      inputMode="decimal"
                      max={220}
                      min={40}
                      onChange={(event) => {
                        setManualBpmInput(event.target.value)
                        setManualBpmSource(event.target.value.trim() ? 'number' : null)
                      }}
                      placeholder={props.track.detected_bpm !== null ? props.track.detected_bpm.toString() : 'Set BPM'}
                      type="number"
                      value={manualBpmInput}
                    />
                    <div className={styles.bpmTapPanel}>
                      <Button className={`${styles.desktopTapButton} ${styles.tapButton}`} onClick={handleTapBpmBeat} variant="primary">
                        {tapPulseKey > 0 && <span aria-hidden="true" className={styles.tapPulse} key={tapPulseKey} />}
                        <Metronome size={16} /> {tapButtonLabel}
                      </Button>
                    </div>
                  </div>
                  <span className="form-label-hint">{bpmHelperText}</span>
                  {manualBpmInvalid && (
                    <span className="form-error">BPM must be from 40 to 220.</span>
                  )}
                </div>
              </div>
              <div className={styles.metadataStrip} aria-label="File metadata">
                {metadataItems.map((item) => {
                  const Icon = metadataIcons[item.key]

                  return (
                    <span className={styles.metadataItem} key={item.key} title={item.label}>
                      <Icon size={14} />
                      <span>{item.value}</span>
                    </span>
                  )
                })}
                {props.track.detected_bpm !== null && (
                  <span className={styles.metadataItem} title="Auto BPM">
                    <Metronome size={14} />
                    <span>Auto BPM {formatBpmValue(props.track.detected_bpm)}</span>
                  </span>
                )}
              </div>
            </div>
            <div className={styles.losslessEditor}>
              <div className={styles.losslessCopy}>
                <span className="form-label">Lossless source</span>
                <strong>{losslessStatusLabel}</strong>
                {trackWithImage.lossless_status === 'ready' && (
                  <small>
                    {losslessFileName ?? 'FLAC source'}
                    {trackWithImage.lossless_size_bytes !== null ? ` · ${formatBytes(trackWithImage.lossless_size_bytes)}` : ''}
                    {trackWithImage.lossless_loudness_lufs !== null ? ` · ${trackWithImage.lossless_loudness_lufs.toFixed(1)} LUFS` : ''}
                  </small>
                )}
                {trackWithImage.lossless_status === 'processing' && (
                  <small>Preserved source is being prepared.</small>
                )}
                {trackWithImage.lossless_status === 'missing' && (
                  <small>Upload a WAV or FLAC source for lossless playback.</small>
                )}
                {trackWithImage.lossless_status === 'failed' && trackWithImage.lossless_error && (
                  <small className="form-error">{trackWithImage.lossless_error}</small>
                )}
                {losslessUploadError && <small className="form-error">{losslessUploadError}</small>}
              </div>
              <div className={styles.losslessActions}>
                <Button
                  disabled={uploadingLossless || deletingLossless}
                  icon={uploadingLossless ? <Loader2 size={16} /> : <Sparkles size={16} />}
                  onClick={() => losslessInputRef.current?.click()}
                >
                  {uploadingLossless ? 'Uploading...' : 'Upload WAV/FLAC'}
                </Button>
                {trackWithImage.lossless_status !== 'missing' && (
                  <IconButton
                    disabled={uploadingLossless || deletingLossless}
                    icon={deletingLossless ? <Loader2 size={15} /> : <Trash2 size={15} />}
                    label="Remove FLAC from track"
                    onClick={() => setLosslessDeleteDialogOpen(true)}
                    title="Remove FLAC"
                    size="sm"
                    variant="danger"
                  />
                )}
              </div>
              <input
                accept=".wav,.flac,audio/wav,audio/x-wav,audio/flac,audio/x-flac"
                hidden
                onChange={(event) => void uploadLosslessSource(event)}
                ref={losslessInputRef}
                type="file"
              />
            </div>
            <div className={styles.sectionPanel}>
              {trackWithImage.suno_prompt_binding && !isEditingLinkedPrompt ? (
                <div className={styles.promptBinding}>
                  <div className={styles.promptBindingHeader}>
                    <div className={styles.promptBindingTitle}>
                      <span className="form-label">Linked library prompt</span>
                      <strong>
                        {trackWithImage.suno_prompt_binding.current_prompt_title ?? trackWithImage.suno_prompt_binding.prompt_snapshot.title}
                        {trackWithImage.suno_prompt_binding.current_prompt_title && trackWithImage.suno_prompt_binding.current_prompt_title !== trackWithImage.suno_prompt_binding.prompt_snapshot.title && (
                          <span className={styles.promptBindingChanged} title={`Title changed from: ${trackWithImage.suno_prompt_binding.prompt_snapshot.title}`}> (edited)</span>
                        )}
                      </strong>
                    </div>
                    <div className={styles.promptBindingActions}>
                      <Button
                        icon={<Pencil size={16} />}
                        onClick={enterPromptEditMode}
                      >
                        Edit
                      </Button>
                      <Button
                        disabled={unlinking}
                        icon={unlinking ? <Loader2 className="tools-spin" size={16} /> : <Unlink size={16} />}
                        onClick={() => void unlinkPrompt()}
                      >
                        Unlink
                      </Button>
                    </div>
                  </div>
                  <div className={styles.promptBindingPreview}>
                    {trackWithImage.suno_prompt_binding.prompt_snapshot.description && (
                      <small>{trackWithImage.suno_prompt_binding.prompt_snapshot.description}</small>
                    )}
                    <small className={styles.promptBindingText}>{trackWithImage.suno_prompt_binding.prompt_snapshot.style_prompt}</small>
                  </div>
                </div>
              ) : (
                <label className="form-group">
                <span className={uploadStyles['upload-prompt-label-row']}>
                  <span className="form-label">Suno style prompt</span>
                  <Button
                    className="upload-save-prompt-button"
                    disabled={!currentStylePrompt}
                    icon={<Save size={16} />}
                    onClick={openSavePromptDialog}
                  >
                    Save to library
                  </Button>
                  <Button
                    className={uploadClass('upload-check-library-button')}
                    disabled={!canCheckPromptLibrary}
                    icon={checkingPromptLibrary ? <Loader2 className="tools-spin" size={16} /> : <Library size={16} />}
                    onClick={() => void checkPromptLibrary()}
                  >
                    Check library
                  </Button>
                </span>
                <textarea
                  className={css('form-input', styles.promptInput)}
                  onChange={(event) => {
                    setStylePrompt(event.target.value)
                    setPromptLibraryMessage(null)
                    resizeStylePromptInput()
                  }}
                  placeholder="Paste the original Suno style prompt"
                  ref={stylePromptRef}
                  rows={3}
                  value={stylePrompt}
                />
                {promptLibraryMessage && <span className="form-label-hint">{promptLibraryMessage}</span>}
                </label>
              )}
            </div>
            <div className={styles.sectionPanel}>
              <div className="form-group track-groups-panel">
                <span className="form-label">Groups</span>
                <ChipSelect
                  empty={<span className="muted">No audio groups created.</span>}
                  onToggle={toggleGroup}
                  options={props.groups.map((group) => ({ id: group.id, label: group.name }))}
                  selectedIds={groupIds}
                />
              </div>
            </div>
          </div>
          <aside className={`form-group ${styles.labelColumn}`}>
            <div className={labelUiStyles.header}>
              <div className={labelUiStyles.title}>
                <span className="form-label">Labels</span>
                {selectedLabels.length > 0 && (
                  <span className={labelUiStyles.count} aria-label={`${selectedLabels.length} selected`}>
                    {selectedLabels.length}
                  </span>
                )}
              </div>
              <div className={labelUiStyles.primaryActions}>
                <Button
                  className={labelUiStyles.browse}
                  icon={<ListFilter size={16} />}
                  onClick={openLabelBrowser}
                  aria-label="Add labels"
                  variant="primary"
                >
                  <span className={labelUiStyles.actionText}>Add labels</span>
                </Button>
                <Button
                  className={labelUiStyles.suggestions}
                  icon={<Sparkles size={16} />}
                  onClick={() => setSuggestionsDialogOpen(true)}
                  aria-label="Open label suggestions"
                >
                  <span className={labelUiStyles.actionText}>Suggestions</span>
                  {suggestionCount > 0 && (
                    <span className={labelUiStyles.badge} aria-label={`${suggestionCount} pending`}>
                      {suggestionCount}
                    </span>
                  )}
                </Button>
              </div>
            </div>
            {selectedLabels.length > 0 ? (
              <div className={labelUiStyles.appliedBody} aria-label="Selected labels">
                {groupedSelectedLabels.map((group) => (
                  <div className={labelUiStyles.appliedGroup} key={group.category}>
                    <span className={labelUiStyles.categoryTitle}>{labelCategoryLabels[group.category]}</span>
                    <div className={labelUiStyles.selectedLabels}>
                      {group.items.map((label) => (
                        <button
                          className={labelUiStyles.selectedLabel}
                          key={label.id}
                          onClick={() => setLabelAssignments((current) => current.filter((item) => item.labelId !== label.id))}
                          title={`Remove ${label.name}`}
                          type="button"
                        >
                          <span>{label.name}</span>
                          <X size={13} />
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className={labelUiStyles.collapsed}>
                No labels selected. Use Add labels to pick from the dictionary, or paste labels copied from another track.
              </div>
            )}
            <div className={labelUiStyles.utilityBar} aria-label="Label utilities">
              <button
                className={labelUiStyles.iconButton}
                disabled={selectedLabels.length === 0}
                onClick={() => void copySelectedLabels()}
                title="Copy selected labels"
                aria-label="Copy selected labels"
                type="button"
              >
                <Clipboard size={15} />
              </button>
              <button
                className={labelUiStyles.iconButton}
                onClick={() => void pasteLabels()}
                title="Paste labels from clipboard"
                aria-label="Paste labels from clipboard"
                type="button"
              >
                <ClipboardPaste size={15} />
              </button>
              <button
                className={labelUiClass(labelUiStyles.iconButton, labelUiStyles.iconButtonDanger)}
                disabled={selectedLabels.length === 0}
                onClick={() => {
                  setLabelAssignments([])
                  setLabelClipboardMessage(null)
                }}
                title="Clear all labels"
                aria-label="Clear all labels"
                type="button"
              >
                <Trash2 size={15} />
              </button>
              {labelClipboardMessage && (
                <span className={styles.labelUtilityMessage}>{labelClipboardMessage}</span>
              )}
            </div>
          </aside>
        </div>
        <div className="modal-actions">
          <Button onClick={props.onClose}>Cancel</Button>
          <Button disabled={saving || manualBpmInvalid} type="submit" variant="primary">
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </form>

        {labelBrowserOpen && (
          <LabelBrowser
            actions={(
              <>
                <Button onClick={closeLabelBrowser}>Cancel</Button>
                <Button disabled={browseAddedCount === 0} onClick={applyBrowseLabels} variant="primary">
                  Add selected{browseAddedCount > 0 ? ` ${browseAddedCount}` : ''}
                </Button>
              </>
            )}
            ariaLabel="Browse labels"
            body={(
              <div className={lb('label-browser-list')}>
                {props.labels.length === 0 && <span className="muted">Create labels first to classify tracks.</span>}
                {labelCategoryOptions.map((category) => {
                  if (browseCategory !== 'all' && browseCategory !== category) {
                    return null
                  }

                  const categoryLabels = props.labels.filter((label) =>
                    label.category === category
                    && label.status === 'active'
                    && matchesLabelSearch(label)
                  )

                  if (categoryLabels.length === 0) {
                    return null
                  }

                  return (
                    <div className={labelUiStyles.category} key={category}>
                      <span className={labelUiStyles.categoryTitle}>{labelCategoryLabels[category]}</span>
                      <div className={lb('label-browser-grid')}>
                        {categoryLabels.map((label) => {
                          const alreadySelected = labelIds.includes(label.id)
                          const selected = browseSelectedLabelIds.includes(label.id)

                          return (
                            <button
                              key={label.id}
                              className={lb('label-browser-item', selected && 'selected')}
                              disabled={alreadySelected}
                              onClick={() => {
                                setBrowseSelectedLabelIds((current) =>
                                  current.includes(label.id)
                                    ? current.filter((id) => id !== label.id)
                                    : [...current, label.id]
                                )
                              }}
                              type="button"
                            >
                              <span className="track-suggestion-check" aria-hidden="true">
                                {selected && <Check size={13} />}
                              </span>
                              <span>
                                <strong>{label.name}</strong>
                                <em>{labelCategoryLabels[label.category]}</em>
                              </span>
                              {alreadySelected && <small>Applied</small>}
                            </button>
                          )
                        })}
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
            description={browseAddedCount > 0 ? `${browseAddedCount} new selected` : 'Select labels to add to this track.'}
            onClose={closeLabelBrowser}
            title="Browse labels"
            tools={(
              <>
                <div className={lb('search-field', 'label-browser-search')}>
                  <Search size={16} />
                  <input
                    autoFocus
                    onChange={(event) => setLabelSearch(event.target.value)}
                    placeholder="Search labels..."
                    value={labelSearch}
                  />
                </div>
                <div className={lb('label-browser-categories')} aria-label="Label categories">
                  <button
                    className={lb('label-browser-category', browseCategory === 'all' && 'active')}
                    onClick={() => setBrowseCategory('all')}
                    type="button"
                  >
                    All
                  </button>
                  {labelCategoryOptions.map((category) => (
                    <button
                      className={lb('label-browser-category', browseCategory === category && 'active')}
                      key={category}
                      onClick={() => setBrowseCategory(category)}
                      type="button"
                    >
                      {labelCategoryLabels[category]}
                    </button>
                  ))}
                </div>
              </>
            )}
          />
        )}
        {suggestionsDialogOpen && (
          <LabelBrowser
            actions={<Button onClick={() => setSuggestionsDialogOpen(false)} variant="primary">Done</Button>}
            ariaLabel="Label suggestions"
            body={(
              <div className={styles.suggestionsBody}>
                <section className={styles.suggestionsSection}>
                  <div className={styles.suggestionsSectionHead}>
                    <div className={styles.suggestionsSectionTitle}>
                      <span className="form-label">Essentia</span>
                      <span className={`${styles.suggestionsStatus} ${audioAnalysisStatusClass(audioAnalysis.status)}`}>
                        {audioAnalysisPolling
                          ? 'Analyzing...'
                          : audioAnalysis.status === 'queued'
                            ? 'Queued'
                            : audioAnalysis.status === 'processing'
                              ? 'Running'
                              : audioAnalysis.status === 'failed'
                                ? 'Failed'
                                : audioAnalysis.status === 'completed'
                                  ? audioSuggestionCount > 0
                                    ? `${audioSuggestionCount} pending`
                                    : 'No new labels'
                                  : 'Not analyzed yet'}
                      </span>
                    </div>
                    {audioSuggestionCount > 0 && (
                      <Button
                        disabled={audioAnalysisPolling}
                        icon={<RefreshCw size={16} />}
                        onClick={(event) => {
                          event.preventDefault()
                          event.stopPropagation()
                          void analyzeAudio()
                        }}
                      >
                        Re-analyze
                      </Button>
                    )}
                  </div>
                  {audioAnalysis.status === 'failed' && (
                    <div className={css(suggestionStyles.message, suggestionStyles.danger)}>
                      <TriangleAlert size={15} /> {getAudioAnalysisMessage(audioAnalysis)}
                    </div>
                  )}
                  {audioSuggestionCount > 0 ? (
                    <div className={css(suggestionStyles.list, styles.suggestionsList)} aria-label="Audio label suggestions">
                      {suggestedTrackLabels.filter((assignment) => assignment.source === 'audio_ai').map((assignment) => (
                        <div className={css(suggestionStyles.item, suggestionStyles.persisted, styles.suggestionItem, styles.persistedSuggestionItem)} key={assignment.id}>
                          <span className={css(suggestionStyles.copy, styles.suggestionCopy)}>
                            <span>
                              <strong>{assignment.label.name}</strong>
                              <span>{labelCategoryLabels[assignment.label.category]}</span>
                            </span>
                            {assignment.reason && <em>{assignment.reason}</em>}
                          </span>
                          <span className={css(suggestionStyles.score, styles.suggestionScore)}>
                            {assignment.confidence ? `${Math.round(Number(assignment.confidence) * 100)}%` : ''}
                          </span>
                          <div className={css(suggestionStyles.inlineActions, styles.suggestionInlineActions)}>
                            <Button onClick={() => rejectPersistedSuggestion(assignment)} size="compact">Reject</Button>
                            <Button onClick={() => acceptPersistedSuggestion(assignment)} size="compact" variant="primary">Accept</Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className={styles.suggestionsEmpty}>
                      <p className={styles.suggestionsEmptyHint}>
                        {audioAnalysisMessage
                          ?? (loadingTrackLabels
                            ? 'Checking Essentia suggestions...'
                            : getAudioAnalysisMessage(audioAnalysis))}
                      </p>
                      <Button
                        className={styles.suggestionsEmptyCta}
                        disabled={audioAnalysisPolling}
                        icon={<RefreshCw size={16} />}
                        onClick={(event) => {
                          event.preventDefault()
                          event.stopPropagation()
                          void analyzeAudio()
                        }}
                        variant="primary"
                      >
                        {audioAnalysisPolling ? 'Analyzing...' : 'Analyze with Essentia'}
                      </Button>
                    </div>
                  )}
                </section>

                <section className={styles.suggestionsSection}>
                  <div className={styles.suggestionsSectionHead}>
                    <div className={styles.suggestionsSectionTitle}>
                      <span className="form-label">From style prompt</span>
                      <span className={styles.suggestionsStatus}>
                        {loadingSuggestions
                          ? 'Suggesting...'
                          : labelSuggestions.length > 0
                            ? `${labelSuggestions.length} pending`
                            : currentStylePrompt
                              ? 'Ready'
                              : 'No prompt yet'}
                      </span>
                    </div>
                    {labelSuggestions.length > 0 && (
                      <Button
                        disabled={!canSuggestLabels}
                        icon={<Sparkles size={16} />}
                        onClick={() => void suggestLabels()}
                      >
                        Re-suggest
                      </Button>
                    )}
                  </div>
                  {suggestionError && (
                    <div className={suggestionStyles.message}>{suggestionError}</div>
                  )}
                  {labelSuggestions.length > 0 ? (
                    <div className={css(suggestionStyles.list, styles.suggestionsList)} aria-label="Prompt label suggestions">
                      {labelSuggestions.map((suggestion) => {
                        const selected = selectedSuggestionIds.includes(suggestion.label_id)

                        return (
                          <button
                            className={css(suggestionStyles.item, styles.suggestionItem, selected && suggestionStyles.selected)}
                            key={suggestion.label_id}
                            onClick={() => {
                              setSelectedSuggestionIds((current) =>
                                current.includes(suggestion.label_id)
                                  ? current.filter((id) => id !== suggestion.label_id)
                                  : [...current, suggestion.label_id]
                              )
                            }}
                            type="button"
                          >
                            <span className="track-suggestion-check" aria-hidden="true">
                              {selected && <Check size={13} />}
                            </span>
                            <span className={css(suggestionStyles.copy, styles.suggestionCopy)}>
                              <span>
                                <strong>{suggestion.label.name}</strong>
                                <span>{labelCategoryLabels[suggestion.label.category]}</span>
                              </span>
                              <em>{suggestion.reason}</em>
                            </span>
                            <span className={css(suggestionStyles.score, styles.suggestionScore)}>
                              {Math.round(suggestion.confidence * 100)}%
                            </span>
                          </button>
                        )
                      })}
                      <div className={suggestionStyles.actions}>
                        <Button
                          onClick={() => {
                            setSelectedSuggestionIds(labelSuggestions.map((suggestion) => suggestion.label_id))
                          }}
                        >
                          Select all
                        </Button>
                        <Button
                          disabled={selectedSuggestionIds.length === 0}
                          onClick={applySelectedSuggestions}
                          variant="primary"
                        >
                          Apply selected
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className={styles.suggestionsEmpty}>
                      <p className={styles.suggestionsEmptyHint}>
                        {currentStylePrompt
                          ? 'LLM uses the current style prompt to suggest matching labels.'
                          : 'Add a Suno style prompt to the track first, then come back here to get suggestions.'}
                      </p>
                      <Button
                        className={styles.suggestionsEmptyCta}
                        disabled={!canSuggestLabels}
                        icon={<Sparkles size={16} />}
                        onClick={() => void suggestLabels()}
                        variant="primary"
                      >
                        {loadingSuggestions ? 'Suggesting...' : 'Suggest from prompt'}
                      </Button>
                    </div>
                  )}
                </section>
              </div>
            )}
            className={styles.suggestionsPanel}
            closeLabel="Close label suggestions"
            description={suggestionCount > 0
              ? `${suggestionCount} pending suggestion${suggestionCount === 1 ? '' : 's'}`
              : 'Pick AI suggestions from Essentia analysis or your style prompt.'}
            onClose={() => setSuggestionsDialogOpen(false)}
            title="Label suggestions"
          />
        )}
        {losslessDeleteDialogOpen && (
          <Modal
            ariaLabelledBy="track-lossless-delete-title"
            backdropClassName={lb('label-browser-backdrop')}
            isDismissDisabled={deletingLossless}
            onClose={() => setLosslessDeleteDialogOpen(false)}
            panelClassName={[deleteConfirmStyles.panel, styles.losslessDeletePanel].join(' ')}
          >
            <div className="modal-heading">
              <span className={deleteConfirmStyles.icon}><Trash2 size={18} /></span>
              <div className={deleteConfirmStyles.copy}>
                <h2 id="track-lossless-delete-title">Remove FLAC?</h2>
                <p>
                  This clears the preserved FLAC source for <strong>{trackWithImage.title}</strong>.
                  MP3 playback and track metadata stay untouched.
                </p>
              </div>
            </div>
            <ModalActions>
              <Button disabled={deletingLossless} onClick={() => setLosslessDeleteDialogOpen(false)} variant="secondary">
                Cancel
              </Button>
              <Button disabled={deletingLossless} onClick={() => void deleteLosslessSource()} variant="danger">
                {deletingLossless ? 'Removing...' : 'Remove FLAC'}
              </Button>
            </ModalActions>
          </Modal>
        )}
      <AudioTrackMobileSheet
        artist={artist}
        bpmHelperText={bpmHelperText}
        canCheckPromptLibrary={canCheckPromptLibrary}
        checkingPromptLibrary={checkingPromptLibrary}
        checkPromptLibrary={checkPromptLibrary}
        copySelectedLabels={copySelectedLabels}
        currentStylePrompt={currentStylePrompt}
        deleteImage={deleteImage}
        deletingLossless={deletingLossless}
        enterPromptEditMode={enterPromptEditMode}
        groupIds={groupIds}
        groups={props.groups}
        handleTapBpmBeat={handleTapBpmBeat}
        imageUrl={imageUrl}
        isEditingLinkedPrompt={isEditingLinkedPrompt}
        labelClipboardMessage={labelClipboardMessage}
        losslessFileName={losslessFileName}
        losslessStatusLabel={losslessStatusLabel}
        losslessUploadError={losslessUploadError}
        manualBpmInput={manualBpmInput}
        manualBpmInvalid={manualBpmInvalid}
        metadataItems={metadataItems}
        mobileEditorTab={mobileEditorTab}
        mobileImageInputRef={mobileImageInputRef}
        mobileLosslessInputRef={mobileLosslessInputRef}
        onClose={props.onClose}
        openLabelBrowser={openLabelBrowser}
        openSavePromptDialog={openSavePromptDialog}
        pasteLabels={pasteLabels}
        promptLibraryMessage={promptLibraryMessage}
        resizeStylePromptInput={resizeStylePromptInput}
        saveTrack={saveTrack}
        saving={saving}
        selectedLabels={selectedLabels}
        setArtist={setArtist}
        setGroupIds={setGroupIds}
        setLabelAssignments={setLabelAssignments}
        setLabelClipboardMessage={setLabelClipboardMessage}
        setLosslessDeleteDialogOpen={setLosslessDeleteDialogOpen}
        setManualBpmInput={setManualBpmInput}
        setManualBpmSource={setManualBpmSource}
        setMobileEditorTab={setMobileEditorTab}
        setPromptLibraryMessage={setPromptLibraryMessage}
        setStylePrompt={setStylePrompt}
        setSuggestionsDialogOpen={setSuggestionsDialogOpen}
        setTitle={setTitle}
        stylePrompt={stylePrompt}
        stylePromptRef={stylePromptRef}
        suggestionCount={suggestionCount}
        tapButtonLabel={tapButtonLabel}
        tapPulseKey={tapPulseKey}
        title={title}
        track={props.track}
        trackWithImage={trackWithImage}
        unlinking={unlinking}
        unlinkPrompt={unlinkPrompt}
        updatingImage={updatingImage}
        uploadImage={uploadImage}
        uploadingLossless={uploadingLossless}
        uploadLosslessSource={uploadLosslessSource}
      />

      {promptEditorState && (
        <PromptEditorDialog
          groups={props.promptGroups}
          isSaving={savingPrompt}
          onCancel={() => {
            setPromptEditorError(null)
            setPromptEditorState(null)
          }}
          onChange={(nextValue) => {
            setPromptEditorError(null)
            setPromptEditorState(nextValue)
          }}
          onGenerateMetadata={async (input) => {
            const result = await suggestSunoPromptMetadata({
              style_prompt: input.stylePrompt,
              exclude_prompt: input.excludePrompt || null
            })

            return {
              title: result.title,
              description: result.description
            }
          }}
          onSave={(input) => void savePromptFromTrack(input)}
          saveError={promptEditorError}
          value={promptEditorState}
        />
      )}
    </div>
  )
}
